import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';

import { GetCatalogItemResponse } from '@services/merch/types/api/get-catalog-item';

import {
  merchReleaseStepCompletedEvent,
  merchReleaseStepViewedEvent,
} from '@webapp/utils/rudderstack/events';

import AppState from '@webapp/store/state';
import { CHANGE_MERCH_TYPE } from '@webapp/store/merch/actions';
import {
  ChangeMerchColor,
  GetCatalogItem,
  UpdateMerchStore,
} from '@webapp/store/merch/action-creators';

import LoadingCtrl from '@webapp/controllers/loading';
import MerchColorList, {
  MerchColorOptions,
} from '@webapp/controllers/merch/merch-color-list';
import MerchSizeSelector, {
  MerchSizeSelectorOptions,
} from '@webapp/controllers/merch/merch-size-select';

import Box from '@components/layout/box';
import Grid from '@components/basics/grid';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import CounterFixedButton from '@components/actions/counter-fixed-button';

interface MerchColorSizePageProps {
  className?: string;
  isEdit?: boolean;
  isMobile?: boolean;
  isTablet?: boolean;
}

interface MerchColorSizeStateProps {
  colors: MerchColorOptions;
  sizes: MerchSizeSelectorOptions;
  printifyProductId?: number;
  productId?: string;
  colorSize: GetCatalogItemResponse | undefined;
  isChangeProductLoading?: boolean;
  artistId?: string;
  artistName?: string;
  updateSelectedColor: (colors: string[]) => ChangeMerchColor;
  updateSelectedSizes: (sizes: string[]) => UpdateMerchStore;
  listCatalogItem: (itemId: number) => GetCatalogItem;
  nextSection: () => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => ({
  artistId: state.artist.active?.id,
  artistName: state.artist.active?.name,
  isChangeProductLoading: !!state.ui.loading[CHANGE_MERCH_TYPE],
  colorSize: state.merch.active?.colorSize,
  printifyProductId: state.merch.active?.editProduct
    ? state.merch.active?.editProduct.printify[0].itemId
    : state.merch.active?.product?.id,
  productId:
    state.merch.active?.editProduct?.id ||
    state.merch.active?.newProduct?.productId,
  sizes: (state.merch.active?.colorSize?.sizes || []).map((item) => {
    return {
      ...item,
      selected: state.merch.active?.selectedSizes
        ? state.merch.active.selectedSizes?.some((val) => val === item.label)
        : true,
    };
  }),
  colors: (state.merch.active?.colorSize?.colors || []).map((item) => {
    return {
      ...item,
      productImgSrc: item.src,
      selected: state.merch.active?.selectedColors
        ? state.merch.active.selectedColors?.some((val) => val === item.label)
        : false,
    };
  }),
  isMobile: state.ui.mobile,
  isTablet: state.ui.tablet,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  nextSection: () => dispatch(push('./design')),
  listCatalogItem: (itemId: number) => dispatch(new GetCatalogItem(itemId)),
  updateSelectedColor: (colors: string[]) =>
    dispatch(new ChangeMerchColor(colors)),
  updateSelectedSizes: (sizes: string[]) =>
    dispatch(new UpdateMerchStore({ selectedSizes: sizes })),
});

export const MerchColorSizePage = ({
  className,
  printifyProductId,
  productId,
  colorSize,
  nextSection,
  listCatalogItem,
  updateSelectedColor,
  updateSelectedSizes,
  sizes,
  colors,
  isChangeProductLoading,
  artistId,
  artistName,
  isEdit,
  isMobile,
  isTablet,
}: MerchColorSizePageProps & MerchColorSizeStateProps) => {
  React.useEffect(() => {
    if (!colorSize && printifyProductId) {
      listCatalogItem(printifyProductId);
    }
  }, [colorSize, printifyProductId, listCatalogItem]);

  React.useEffect(() => {
    if (productId) {
      merchReleaseStepViewedEvent({
        step: 2,
        name: 'select color-size',
        artist_id: artistId,
        artist_name: artistName,
        product_type: printifyProductId,
        is_edit: isEdit,
        product_id: productId,
      });
    }
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleColorChange = (data: MerchColorOptions) => {
    const currentSelectedColor =
      data.filter((color) => color.selected).map((color) => color.label) || [];

    const previousSelectedColors =
      colors.filter((color) => color.selected).map((color) => color.label) ||
      [];
    if (!previousSelectedColors.includes(currentSelectedColor[0])) {
      updateSelectedColor([...previousSelectedColors, currentSelectedColor[0]]);
    } else {
      updateSelectedColor(
        previousSelectedColors.filter(
          (prev) => prev !== currentSelectedColor[0]
        )
      );
    }
  };

  const handleSizeChange = (data: MerchSizeSelectorOptions) => {
    updateSelectedSizes(
      data.filter((size) => size.selected).map((size) => size.label)
    );
  };

  const handleNextStepClick = () => {
    merchReleaseStepCompletedEvent({
      step: 2,
      name: 'select color-size',
      artist_id: artistId,
      artist_name: artistName,
      product_type: printifyProductId,
      is_edit: isEdit,
      product_id: productId,
      sizes: sizes.filter((size) => size.selected).map((size) => size.label),
      colors: colors
        .filter((size) => size.selected)
        .map((color) => color.label),
    });
    nextSection();
  };

  if (isChangeProductLoading) {
    return <LoadingCtrlWrapper size="160px" loading={isChangeProductLoading} />;
  }
  return colorSize && !isChangeProductLoading ? (
    <MotionOpacity className={className}>
      {!isMobile && (
        <Typography className="mb-30" customFont="semi-bold" type="subheader">
          <Translate id="MERCH.SECTIONS.COLOR.TITLE" />
        </Typography>
      )}

      <Grid gap={3}>
        <Box noBorderBottom={sizes && sizes.length > 1}>
          <Typography className="mb-20" customFont="medium" type="big-title">
            <Translate id="MERCH.SECTIONS.COLOR.AVAILABLE.COLORS" />
          </Typography>
          <MerchColorList
            productId={printifyProductId}
            variants={colors}
            onChange={handleColorChange}
          />
        </Box>
        {sizes && sizes.length > 1 && (
          <Box noBorderTop>
            <Typography className="mb-20" customFont="medium" type="big-title">
              <Translate id="MERCH.SECTIONS.COLOR.AVAILABLE.SIZES" />
            </Typography>
            <MerchSizeSelector sizes={sizes} onChange={handleSizeChange} />
          </Box>
        )}
      </Grid>

      <CounterFixedButton
        id="colorSize"
        className={!isTablet ? '' : 'mt-30'}
        fixed={!isTablet}
        isValid={
          !!colors &&
          !!colors.find((color) => color.selected) &&
          !!sizes &&
          !!sizes.find((size) => size.selected) &&
          !isChangeProductLoading
        }
        currentStep={2}
        maxStep={4}
        isLoading={false}
        onNext={handleNextStepClick}
      />
    </MotionOpacity>
  ) : null;
};

export default connect(mapStateToProps, mapDispatchToProps)(MerchColorSizePage);

const LoadingCtrlWrapper = styled(LoadingCtrl)`
  margin: 30px auto;
  display: flex;
  justify-content: center;
`;
